import 'firebase/compat/auth';
import axios from 'axios';
import firebase from 'firebase/compat/app';
import endpoints from 'constants/endpoints';
import { getCompany } from 'helpers/getCompanyVariables';
import createUserState, { createZohoData } from 'helpers/factory/userFactory';
import { postApiCall, safePostCall } from 'helpers/firebaseSetup';
import { apiRequest, ApiError } from 'lib/api';
import {
  parseZohoJsonOutput,
  parseZohoOkMessageOutput
} from 'helpers/zoho/response-parsers';
import { siteLoaded } from './actions';
import { showAlert, showAlertModal, siteLoading } from './site';

const company = getCompany();

import {
  GET_USER,
  LOG_IN,
  REFRESH_USER,
  SHOW_ALERT,
  SET_VIEWING_AS,
  SET_USER_DATA,
  RESET_VIEWING_AS,
  IS_SENDING_INFORMATION,
  SET_COMPLETED_TOUR_SECTIONS
} from 'constants/types';

export const setCompletedTourSections = sections => ({
  type: SET_COMPLETED_TOUR_SECTIONS,
  payload: sections
});

/**
 * marks the session as viewing the portal as another agent; refreshUser loads the profile
 * @param agentID - zoho id of the agent to impersonate
 */
export function setViewingAsAgent(agentID) {
  return { type: SET_VIEWING_AS, payload: agentID };
}

/**
 * leaves view-as. the requester's profile comes back with the next refreshUser,
 * which reads the identity from the firebase user, so nothing else is needed here
 */
export function resetViewingAsAgent() {
  return { type: RESET_VIEWING_AS };
}

export const setIsSendingInformation = payload => async dispatch => {
  dispatch({ type: IS_SENDING_INFORMATION, payload });
};

export const getUser = (firebaseData, userData, setUserData) => {
  return async dispatch => {
    safePostCall(endpoints.awsProxy.safePost, {
      googleId: firebaseData.user.uid,
      zohoId: false,
      endpoint: endpoints.zoho.getAgent,
      isZohoRequest: true
    })
      .then(async zohoRes => {
        const result = parseZohoJsonOutput(zohoRes?.data?.details?.output);
        if (!result.ok) {
          setUserData({
            ...userData,
            errors: {
              ...userData.errors,
              email: result.message || 'User not found'
            }
          });
          dispatch(siteLoaded());
          return;
        }
        const zohoData = result.data.agent;

        dispatch({
          type: SET_USER_DATA,
          payload: {
            data: createUserState(zohoData, firebaseData),
            mainZohoId: zohoData.id
          }
        });
        dispatch({ type: LOG_IN });
        dispatch(siteLoaded());
        // redirection is handled by PrivateRouteAuth based on redirect query param
      })
      .catch(() => {
        setUserData({
          ...userData,
          errors: {
            ...userData.errors,
            email: 'User not found'
          }
        });
        dispatch(siteLoaded());
      });
  };
};

// true once the caller cancelled the request, e.g. the view unmounted mid-flight.
// safePostCall swallows axios cancellations and resolves to null, so the cancel never
// reaches a catch — the token is the only place left that still knows about it
function wasCancelled(cancelTokenSource) {
  return Boolean(cancelTokenSource?.token?.reason);
}

// the requester's own profile, straight from zoho through the safe proxy
async function fetchOwnAgent(firebaseData, cancelTokenSource) {
  const zohoRes = await safePostCall(
    endpoints.awsProxy.safePost,
    {
      // identity always comes from the logged-in firebase user
      googleId: firebaseData?.user?.uid,
      endpoint: endpoints.zoho.getAgent,
      isZohoRequest: true
    },
    { cancelToken: cancelTokenSource.token }
  );
  cancelTokenSource?.token?.throwIfRequested?.();
  return parseZohoJsonOutput(zohoRes?.data?.details?.output);
}

// another agent's profile through the api, which checks the downline before asking zoho
async function fetchViewAsAgent(targetId) {
  try {
    const body = await apiRequest({
      method: 'POST',
      path: '/agents/view-as',
      data: { agents: [targetId] }
    });
    return parseZohoJsonOutput(body?.details?.output);
  } catch (error) {
    // 4xx carry the rule that failed; anything else is a real failure
    if (error instanceof ApiError && error.status && error.status < 500) {
      return { ok: false, message: error.message, data: null };
    }
    throw error;
  }
}

/**
 * reloads the profile in state.user: the requester's own, or the view-as target's.
 * failures always surface an alert, and a failed view-as load drops back to the requester
 * @param finishedRefresh - callback run once the request settled, success or not
 * @param cancelTokenSource - axios cancel token source for the own-profile request
 */
export function refreshUser(
  finishedRefresh = () => {},
  cancelTokenSource = {}
) {
  return async (dispatch, getState) => {
    const { viewAsAgent, firebaseData } = getState().user;
    const isViewingAsAgent = Boolean(viewAsAgent);

    try {
      const result = isViewingAsAgent
        ? await fetchViewAsAgent(viewAsAgent)
        : await fetchOwnAgent(firebaseData, cancelTokenSource);

      // leaving the page mid-request is not a failure: the null from the cancelled
      // call parses as an empty response, which would alert on the next screen
      if (wasCancelled(cancelTokenSource)) {
        return { ok: false, message: '', data: null };
      }

      if (!result.ok) {
        dispatch(
          showAlert({
            message:
              result.message ||
              (isViewingAsAgent
                ? 'Unable to load that agent.'
                : 'Unable to load your information. Try again later.'),
            code: 'error'
          })
        );
        if (isViewingAsAgent) dispatch(resetViewingAsAgent());
        return result;
      }

      const zohoData = result.data?.agent;
      if (isViewingAsAgent) {
        dispatch({
          type: GET_USER,
          payload: createUserState(zohoData, firebaseData)
        });
      } else {
        dispatch({
          type: REFRESH_USER,
          payload: createZohoData(zohoData)
        });
      }
      return result;
    } catch (error) {
      // ignore cancellations, they aren't real errors
      if (axios.isCancel(error)) return { ok: false, message: '', data: null };
      console.error(error);
      dispatch(
        showAlert({
          message: 'Unable to load your information. Try again later.',
          code: 'error'
        })
      );
      if (isViewingAsAgent) dispatch(resetViewingAsAgent());
      return { ok: false, message: '', data: null };
    } finally {
      // always resolve the loading state so the caller's spinner never sticks
      finishedRefresh();
    }
  };
}

/**
 * keeps the view-as session in sync with the url. ?va= enters (or switches)
 * view-as and loads that profile; without ?va= it only refetches the requester
 * when hydration flagged the stored profile as stale
 * @param search - location.search to read ?va= from
 * @returns {{ denied: boolean }} denied is true when the api rejected the view-as target
 */
export function syncViewAsFromSearch(search) {
  return async (dispatch, getState) => {
    const va = new URLSearchParams(search ?? '').get('va');
    const { viewAsAgent, zohoData, needsRefresh } = getState().user;

    if (!va) {
      if (needsRefresh) await dispatch(refreshUser());
      return { denied: false };
    }

    // already showing that agent, nothing to do
    if (viewAsAgent === va && zohoData?.id === va) return { denied: false };

    dispatch(setViewingAsAgent(va));
    const result = await dispatch(refreshUser());
    return { denied: result?.ok === false };
  };
}

export const changeUserPassword =
  (password, currentPassword, form, navigate) => (dispatch, getState) => {
    const { setFormState, formState } = form;
    const zohoData = getState().user.zohoData;
    const user = firebase.auth().currentUser;

    dispatch(siteLoading());

    const credential = firebase.auth.EmailAuthProvider.credential(
      user.email,
      currentPassword
    );

    user
      .reauthenticateWithCredential(credential)
      .then(() => {
        user
          .updatePassword(password)
          .then(() => {
            dispatch({
              type: REFRESH_USER,
              payload: { ...zohoData, Portal_Password: password }
            });

            dispatch(siteLoaded());

            dispatch({
              type: SHOW_ALERT,
              payload: {
                message: 'Password Updated Successfully',
                code: 'success'
              }
            });
            navigate(-1);
          })
          .catch(error => {
            console.error(error);
            dispatch(siteLoaded());
            dispatch({
              type: SHOW_ALERT,
              payload: {
                message: "There's been an error",
                code: 'error'
              }
            });
          });
      })
      .catch(e => {
        console.error(e);
        setFormState({
          ...formState,
          errors: { ...formState.errors, currentPassword: 'Wrong password' }
        });
        dispatch({
          type: SHOW_ALERT,
          payload: {
            message: "There's been an error",
            code: 'error'
          }
        });
        dispatch(siteLoaded());
      });
  };

export const sendEmailModifyAgentData = () => async (dispatch, getState) => {
  const zohoId = getState().user.zohoData.id;
  dispatch(showAlert({ message: 'Sending Email', code: 'info' }));

  try {
    const response = await safePostCall(endpoints.awsProxy.safePost, {
      endpoint: endpoints.zoho.sendEmailsAgentInfo,
      agentId: zohoId,
      isZohoRequest: true
    });

    if (!response?.data?.details) {
      dispatch(
        showAlert({
          message: 'Something went wrong. Try again later.',
          code: 'error'
        })
      );
      return;
    }

    const { ok, message } = parseZohoOkMessageOutput(
      response.data.details.output
    );

    if (message) {
      dispatch(showAlert({ message, code: ok ? 'success' : 'error' }));
      return;
    }

    if (!ok) {
      dispatch(
        showAlert({
          message: 'Something went wrong. Try again later.',
          code: 'error'
        })
      );
    }
  } catch (error) {
    console.error(error);
    dispatch(
      showAlert({
        message: 'Something went wrong. Try again later.',
        code: 'error'
      })
    );
  }
};

export const sendEmailPurchaseMoreLeads = () => (dispatch, getState) => {
  let zohoId = getState().user.zohoData.id;
  dispatch(showAlert({ message: 'Sending Email', code: 'info' }));
  safePostCall(endpoints.awsProxy.safePost, {
    endpoint: endpoints.zoho.sendPurchaseMoreLeadsForm,
    agentId: zohoId,
    isZohoRequest: true
  })
    .then(() => {
      dispatch(
        showAlert({ message: 'Email sent successfully!', code: 'success' })
      );
    })
    .catch(error => console.error(error));
};

export const resetPasswordByEmail =
  (email, setFormState, initialState) => dispatch => {
    dispatch(showAlert({ message: 'Sending Email', code: 'info' }));
    firebase
      .auth()
      .sendPasswordResetEmail(email)
      .then(() => {
        dispatch(
          showAlert({ message: 'Email Sent Successfully!', code: 'success' })
        );
        setFormState(initialState);
      })
      .catch(error => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error({ errorCode, errorMessage });
        dispatch(
          showAlert({
            message: "There's been an error. Try Again",
            code: 'error'
          })
        );
      });
  };

export const askForAUserRegistration =
  (email, userName, navigate) => dispatch => {
    dispatch(setIsSendingInformation(true));
    dispatch(
      showAlert({
        message: 'Sending your info to owr team',
        code: 'info'
      })
    );
    postApiCall(endpoints.awsProxy.genericPost, {
      endpoint: 'portal_access_request',
      agentEmail: email,
      agentName: userName,
      isZohoRequest: true
    })
      .then(() => {
        dispatch(setIsSendingInformation(false));
        dispatch(
          showAlertModal({
            title: 'Request sent successfully!',
            message: `Your request to join the ${
              company === 'oneill' ? "O'Neill Marketing" : 'Sunshine'
            } Portal has been submitted. You will be contacted shortly to your email (${email}) for next steps.`,
            size: 'small',
            code: 'success',
            buttonText: 'Ok'
          })
        );
        dispatch(
          showAlert({ message: 'Request sent successfully!', code: 'success' })
        );
        navigate(-1);
      })
      .catch(error => {
        console.error(error);
        dispatch(setIsSendingInformation(false));
        dispatch(
          showAlert({
            message:
              'An error ocurred, please try again. If the problem persists contact support',
            code: 'error'
          })
        );
      });
  };

export const changeUserProfilePic =
  (base64Image, agentId, fileInputRef, setLocalLoader, cancelTokenSource) =>
  dispatch => {
    safePostCall(
      endpoints.awsProxy.safePost,
      {
        endpoint: endpoints.zoho.uploadProfilePic,
        agentId,
        content: base64Image,
        isZohoRequest: true
      },
      { headers: { Accept: 'application/json' }, timeout: 5000 }
    )
      .then(() => {
        dispatch(
          refreshUser(() => {
            setLocalLoader(false);
            dispatch(
              showAlert({ message: 'Profile Photo Updated', code: 'success' })
            );
          }, cancelTokenSource)
        );

        fileInputRef.current.value = null;
      })
      .catch(error => {
        let message = "Couldn't Update Profile Photo";
        fileInputRef.current.value = null;

        console.error(error);

        if (JSON.stringify(error).includes('timeout'))
          message = `${message}. File might be too large`;

        dispatch(
          showAlert({
            message,
            code: 'error'
          })
        );
      });
  };
